import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Shield, Clock } from 'lucide-react';

const teamHighlights = [
  {
    icon: Award,
    title: 'Meisterbetrieb',
    description: 'Geführt von einem qualifizierten Elektromeister mit langjähriger Erfahrung – Elektroarbeiten nach aktuellen DIN-VDE-Normen.',
  },
  {
    icon: Users,
    title: 'Erfahrene Fachkräfte',
    description: 'Unser Team aus ausgebildeten Elektrikern arbeitet sauber, präzise und normgerecht.',
  },
  {
    icon: Shield,
    title: 'Persönliche Betreuung',
    description: 'Von der Beratung bis zur Abnahme – ein fester Ansprechpartner für Ihr Projekt.',
  },
  {
    icon: Clock,
    title: 'Zuverlässig & Termingerecht',
    description: 'Wir halten, was wir versprechen – pünktlich, ordentlich und ohne Überraschungen.',
  },
];

export default function TeamSection() {
  return (
    <section id="team" data-testid="team-section" className="py-24 sm:py-32 bg-[#1A3A22] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#2D5A27]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#10B981]/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top: Text links, Bild rechts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-12 sm:mb-16">
          {/* Left - Text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
              Unser Team
            </span>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-white tracking-tight mt-3 mb-6">
              Ein Team, das <span className="text-[#10B981]">Handwerk lebt</span>
            </h2>
            <p className="font-['Manrope'] text-white/70 text-base sm:text-lg leading-relaxed mb-4">
              Hinter Daniels Elektroinstallationen steht ein eingespieltes Team aus erfahrenen
              Elektrikern, einem Elektromeister und Servicekräften. Seit 1966 geben wir unser
              Wissen von Generation zu Generation weiter – mit Erfahrung, Verlässlichkeit und
              einem hohen Anspruch an saubere Elektroarbeit.
            </p>
            <p className="font-['Manrope'] text-white/50 text-base leading-relaxed">
              Jeder in unserem Team teilt den gleichen Anspruch: Arbeit abliefern, die man mit
              gutem Gewissen seinem eigenen Nachbarn empfehlen würde. Sauber, sicher und
              auf den Punkt.
            </p>
          </motion.div>

          {/* Right - Team Bild groß mit Rahmen */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Akzent-Block hinter dem Bild */}
            <div className="absolute -top-4 -left-4 w-full h-full border-2 border-[#10B981]/40 hidden sm:block" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#10B981]/20 hidden sm:block" />
            <img
              src="/team-bild.jpeg"
              alt="Team der K.H. und B. Daniels Elektroinstallationen GmbH mit Firmenfahrzeugen"
              className="relative w-full h-[320px] sm:h-[420px] lg:h-[480px] object-cover border-4 border-white/10 shadow-2xl"
              data-testid="team-image"
            />
          </motion.div>
        </div>

        {/* Bottom: 4 Highlight-Icons in einer Reihe */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {teamHighlights.map((item, idx) => (
            <div
              key={idx}
              data-testid={`team-highlight-${idx}`}
              className="bg-[#2D5A27]/30 border border-[#10B981]/10 p-6 hover:bg-[#2D5A27]/50 hover:-translate-y-1 transition-all duration-300"
            >
              <item.icon className="w-8 h-8 text-[#10B981] mb-4" strokeWidth={1.5} />
              <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-2">
                {item.title}
              </h3>
              <p className="font-['Manrope'] text-white/50 text-sm leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
